import { apiClient } from '../client';

/**
 * W7: carriers other than Twilio, for the super admin's TwilioConfig page.
 *   - SU-4: the Twilio SIP domain fed by Zadarma, and the import of a number
 *     bought by hand on Zadarma (POST /telephony/admin/sip-domain,
 *     /telephony/admin/zadarma-import, guarded on the server by
 *     req.auth.isSuperAdmin);
 *   - SU-3: the estimated cost of the calls placed through Asterisk and of the
 *     Zadarma lines, from the call durations and the tariff table
 *     (/admin/platform/carrier-costs, /admin/platform/carrier-tariffs).
 */

export interface SipDomainResult {
  domainSid: string;
  domainName: string;
  aclSid: string;
  created: boolean;
}

export interface ZadarmaImportResult {
  imported: boolean;
  updated?: true;
  number: { id?: string; phone_number: string; status?: string; sip_uri?: string; caller_id_verification?: string };
}

export function ensureSipDomain(domainName?: string) {
  return apiClient.post<SipDomainResult>('/telephony/admin/sip-domain', domainName?.trim() ? { domain_name: domainName.trim() } : {});
}

export function importZadarmaNumber(body: { tenant_email: string; phone_number: string; customer_id?: string }) {
  return apiClient.post<ZadarmaImportResult>('/telephony/admin/zadarma-import', body);
}

export type CostCarrier = 'asterisk' | 'zadarma';
export type CallDirection = 'outbound' | 'inbound';
export type BillingIncrement = 'per_second' | 'per_minute';

export interface CarrierTariffRow {
  carrier: CostCarrier;
  direction: CallDirection;
  /** E.164 prefix with its +, '' = every other destination. */
  prefix: string;
  label: string;
  /** Price of one minute, in `currency`. */
  per_minute: number;
  increment: BillingIncrement;
}

export interface CarrierTariffs {
  currency: string;
  rates: CarrierTariffRow[];
  /** Monthly fee of one Zadarma number when the number carries none. */
  zadarma_number_monthly: number;
}

export interface CarrierCostLine extends CarrierTariffRow {
  /** False when no rate matched: the calls are counted, their cost is unknown. */
  priced: boolean;
  calls: number;
  seconds: number;
  billed_minutes: number;
  cost: number;
}

export interface CarrierCosts {
  month: string;
  from: string;
  to: string;
  currency: string;
  estimate: true;
  tariffs_source: 'default' | 'setting' | 'invalid_setting';
  tariffs_updated_at: string | null;
  tariffs: CarrierTariffs;
  totals: Record<CostCarrier, { calls: number; seconds: number; cost: number }>;
  zadarma_numbers: { count: number; monthly_cost: number };
  lines: CarrierCostLine[];
  unpriced_calls: number;
  total_cost: number;
}

export function getCarrierCosts(month: string, signal?: AbortSignal) {
  return apiClient.get<CarrierCosts>(`/admin/platform/carrier-costs?month=${encodeURIComponent(month)}`, { signal });
}

export function putCarrierTariffs(tariffs: CarrierTariffs) {
  return apiClient.put<{ tariffs: CarrierTariffs; updated_at: string }>('/admin/platform/carrier-tariffs', tariffs);
}

